<template>
  <div id="app" class="print:hidden">
    <!--
    Even when routes use the same component, treat them
    as distinct and create the component again.
    -->
    <template v-if="isAppLoading"><Loader class="mt-44 mb-40" :content="true" /></template>
    <RouterView v-else />
    <UiToastList />
  </div>
</template>

<script>
import UiToastList from '@components/UiElements/UiToastList.vue'
import appConfig from '@src/app.config'
import { mapActions, mapState } from 'vuex'
import initializePushNotifications from './push-notifications'
import { RouterView } from 'vue-router'
import Loader from '@components/BaseComponent/Loader.vue'

export default {
  name: 'App',
  components: {
    UiToastList,
    RouterView,
    Loader,
  },
  data() {
    return {
      lastState: document.visibilityState,
      socketChannel: null,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.auth.currentUser,
      isAppLoading: (state) => state.layout.isAppLoading,
    }),
  },

  page: {
    titleTemplate(title) {
      let instituteTitle = localStorage.getItem('generalSettingSiteTitle')

      title = typeof title === 'function' ? title(this.$store) : title
      return instituteTitle
        ? title
          ? `${instituteTitle} | ${title}`
          : `${instituteTitle}`
        : title
        ? `${appConfig.title} | ${title}`
        : `${appConfig.title} `
    },
  },

  created() {
    if (this.currentUser) initializePushNotifications()
    const settings = localStorage.getItem('setting') || null
    this.connectTenantNotifierSocket()
    this.getShow()
    this.setLanguage()
    this.initGeneralSetting()
    if (settings) this.loadSiteSettings(JSON.parse(settings))
  },

  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },
  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    this.disconnectSocket()
  },

  methods: {
    connectTenantNotifierSocket() {
      const authToken = localStorage.getItem('jwt') || null

      if (authToken) {
        if (!this.$socket) {
          this.$connectSocket('/tenant-notifire-socket', authToken)
          setTimeout(() => {
            this.connectTenantNotifierSocketChannel()
          }, 2000)
        } else if (!this.$socket.isConnected()) {
          this.$socket.connect()
          setTimeout(() => {
            this.connectTenantNotifierSocketChannel()
          }, 2000)
        } else this.connectTenantNotifierSocketChannel()
      }
    },

    connectTenantNotifierSocketChannel() {
      if (!this.$socket || !this.currentUser?.id) return null

      const channelName = `tenant_notification:${this.currentUser.id}`

      const existingChannel = this.$socket.channels.find((channel) => channel.topic === channelName)

      if (existingChannel && existingChannel.state === 'joined') {
        this.socketChannel = existingChannel
        return existingChannel
      }

      if (this.socketChannel && this.socketChannel.state === 'joined') this.socketChannel.leave()

      const channel = this.$socket.channel(channelName)

      channel.on('notificationcreated', (payload) => {
        if (!this.$socket.isConnected()) return

        const notification = {
          id: payload.id,
          title: payload.title,
          inserted_at: payload.inserted_at,
          status: 'unread',
          redirect_path: payload?.redirect_path,
          description: payload?.body,
          body: payload?.body,
          related_user: {
            related_user_first_name: payload?.related_user_first_name,
            related_user_last_name: payload?.related_user_last_name,
            related_user_image: payload?.related_user_image,
          },
        }

        this.$store.commit(
          'toast/NEW',
          {
            id: notification.id,
            message: notification.title,
            description: notification?.description || '',
            image: notification.related_user.related_user_image,
            related_user: notification.related_user,
            inserted_at: notification.inserted_at,
            type: 'notification',
          },
          { root: true },
        )
      })
      /* ANNOUNCEMENTS */
      channel.on('announcementcreated', (payload) => {
        const payloadAnnouncementCounts = 'increment'
        const newAnnouncement = {
          body: payload.body,
          id: payload.id,
          title: payload.title,
          inserted_at: payload.inserted_at,
          status: 'unread',
          redirect_path: payload?.redirect_path,
          description: payload?.body,
        }
        this.$store.commit('notifications/UPDATE_ANNOUNCEMENT', newAnnouncement)
        this.$store.commit(
          'notifications/SET_UNREAD_ANNOUNCEMENTS_COUNT',
          payloadAnnouncementCounts,
        )

        this.$store.commit(
          'toast/NEW',
          {
            id: newAnnouncement.id,
            message: newAnnouncement.title,
            description: newAnnouncement?.description || '',
            inserted_at: newAnnouncement.inserted_at,
            type: 'success',
          },
          { root: true },
        )
      })

      channel
        .join()
        .receive('ok', ({ message }) => {
          console.log('Joined channel:', message)
          this.socketChannel = channel
        })
        .receive('error', ({ reason }) => console.log('Failed to join channel:', reason))
        .receive('timeout', () => console.log('Channel join timeout'))

      return channel
    },

    handleVisibilityChange() {
      const currentState = document.visibilityState
      if (this.lastState === currentState) return
      this.lastState = currentState

      if (currentState === 'visible') {
        console.log('PWA is in foreground - reconnecting socket')
        this.connectTenantNotifierSocket()
      } else {
        console.log('PWA is in background - disconnecting socket')
        this.disconnectSocket()
      }
    },

    disconnectSocket() {
      // Properly leave the channel first
      if (this.socketChannel && this.socketChannel.state === 'joined') {
        this.socketChannel.leave()
        this.socketChannel = null
      }
      // Disconnect the socket
      if (this.$socket && this.$socket.isConnected()) {
        this.$socket.disconnect()
      }
    },
    setLanguage() {
      const language = JSON.parse(localStorage.getItem('curLanguage')) || {}
      if (language.short) {
        this.$i18n.locale = language.short
      } else {
        this.$i18n.locale = 'en'
        localStorage.setItem(
          'curLanguage',
          JSON.stringify({
            short: 'en',
            long: 'English',
            dir: 'ltr',
          }),
        )
        this.$i18n.locale = 'en'
        this.$store.commit('base/BODY_DIR', 'ltr')
      }
    },

    ...mapActions('users', ['getShow']),
    ...mapActions('settings', ['initGeneralSetting', 'loadSiteSettings']),
  },
}
</script>
